import { useEffect, useState } from 'react';
import { HistoryClock } from '../../components/history/HistoryClock.js';
import { ToolHistorySheet } from '../../components/history/HistorySheet.js';
import { useToolHistory } from '../../hooks/useToolHistory.js';
import { ApiError } from '../../lib/api-error.js';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, SecondaryButton } from '../../components/chrome/Button.js';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell.js';
import { PaymentSheet } from '../../components/payment/PaymentSheet.js';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { useAuth } from '../../store/auth.js';
import { apiFetch } from '../../lib/api-client.js';
import { useSkuInvoice } from '../../hooks/useSkuInvoice.js';
import { useTextResult } from '../../hooks/useTextResult.js';
import { getBaselineSkuRunId } from '../../lib/discover-sku-run.js';
import { ReportMarkdown } from '../../components/ReportMarkdown.js';
import { HowItWorks } from '../../components/HowItWorks.js';
import styles from './When.module.css';

interface TopDate {
  date: string;
  score: number;
  driver: string | null;
}

interface WhenResult {
  id: string;
  question: string;
  horizon: number;
  hasBirthTime?: boolean;
  llmSummary: string | null;
  topDates: TopDate[];
}

type InvoiceSku = 'when_detail_unlock' | 'when_30' | 'when_60';

interface SkuRunRow {
  id: string;
  skuCode: string;
  output: unknown;
  createdAt: string;
}

/**
 * Poll GET /api/v1/sku-runs until a run with id !== baselineRunId appears.
 * Returns the new run's id, or null on timeout.
 */
async function pollForNewRunId(
  jwt: string,
  skuCode: string,
  baselineRunId: string | null,
  opts: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<string | null> {
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const intervalMs = opts.intervalMs ?? 2_000;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    try {
      const res = await apiFetch(
        `/api/v1/sku-runs?skuCode=${encodeURIComponent(skuCode)}&limit=1`,
        jwt,
      );
      if (res.ok) {
        const data = (await res.json()) as { runs?: SkuRunRow[] };
        const run = data.runs?.[0] ?? null;
        if (run && run.id !== baselineRunId) {
          return run.id;
        }
      }
    } catch {
      // transient error — keep polling
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

export function When(): JSX.Element {
  const { t, i18n } = useTranslation(['flows', 'common']);
  const jwt = useAuth((s) => s.jwt);
  const { purchase, redeemCoupon } = useSkuInvoice();
  const history = useToolHistory('when', false);
  const [showHistory, setShowHistory] = useState(false);

  const [question, setQuestion] = useState('');
  const [result, setResult] = useState<WhenResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deep-dive state
  const [diveDate, setDiveDate] = useState<string | null>(null);
  const [diveText, setDiveText] = useState<Record<string, string>>({});
  const [diveLoading, setDiveLoading] = useState<string | null>(null);

  // Invoice state
  const [invoice, setInvoice] = useState<InvoiceSku | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const [paidRunId, setPaidRunId] = useState<string | null>(null);
  const [historyPaidForecast, setHistoryPaidForecast] = useState(false);
  const [awaitingResult, setAwaitingResult] = useState(false);

  const textResult = useTextResult(paidRunId ?? '');

  function clearResult(): void {
    setResult(null); setQuestion(''); setDiveDate(null); setDiveText({});
    setPaidRunId(null); setHistoryPaidForecast(false); setInvoice(null); setShowHistory(false); setError(null); setDiveLoading(null); setAwaitingResult(false);
  }
  useEffect(() => { clearResult(); }, [history.resultId, history.userId]);
  useEffect(() => {
    const saved = history.detail.data;
    if (!history.resultId || !saved || saved.id !== history.resultId) return;
    setResult({ ...saved, topDates: saved.topDates.map(d => ({ ...d, date: d.date.slice(0, 10) })) });
    setQuestion(saved.question);
    setDiveText(saved.deepDiveTexts ?? {});
    setPaidRunId(typeof saved.paidRunId === 'string' && /^[A-Za-z0-9_-]{1,200}$/.test(saved.paidRunId) ? saved.paidRunId : null);
    setHistoryPaidForecast(typeof saved.paidHorizon === 'number' && Number.isFinite(saved.paidHorizon) && saved.paidHorizon > 0);
  }, [history.resultId, history.detail.data]);
  function startNew(): void { clearResult(); history.open(null); }

  async function ask(): Promise<void> {
    if (busy) return;
    if (!jwt || question.trim().length < 3) { setError(t('when.tooShort')); return; }
    setError(null);
    setBusy(true);
    const res = await apiFetch('/api/v1/when/ask', jwt, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ question }),
    });
    setBusy(false);
    if (res.status === 409) { setError(t('when.noChart')); return; }
    if (!res.ok) { setError(t('when.error')); return; }
    const saved = (await res.json()) as WhenResult;
    setResult(saved);
  }

  async function loadDeepDive(date: string): Promise<void> {
    if (!jwt || !result || diveLoading) return;
    if (diveText[date]) { setDiveDate(diveDate === date ? null : date); return; }
    setDiveDate(date);
    setDiveLoading(date);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/when/${result.id}/deep-dive`, jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ date }),
      });
      if (res.status === 402) { setInvoice('when_detail_unlock'); return; }
      if (!res.ok) { setError(t('when.error')); return; }
      const data = (await res.json()) as { dateLabel: string; text: string | null };
      if (data.text) setDiveText((prev) => ({ ...prev, [date]: data.text! }));
    } catch {
      setError(t('when.error'));
    } finally {
      setDiveLoading(null);
    }
  }

  /**
   * After a payment or coupon went through: show a loader while the paid run
   * appears, then show what was bought. The day-breakdown unlock has no text of
   * its own, so it opens the day that hit the paywall instead.
   */
  async function showPaidResult(sku: InvoiceSku, baselineRunId: string | null): Promise<void> {
    setInvoice(null);
    if (!jwt) return;
    const unlockDate = sku === 'when_detail_unlock' ? diveDate : null;
    if (unlockDate) setDiveLoading(unlockDate);
    else setAwaitingResult(true);
    const runId = await pollForNewRunId(jwt, sku, baselineRunId);
    setAwaitingResult(false);
    if (!runId) {
      setDiveLoading(null);
      setError(t('when.error'));
      return;
    }
    if (sku === 'when_detail_unlock') {
      if (unlockDate) await loadDeepDive(unlockDate);
      return;
    }
    setPaidRunId(runId);
  }

  async function doPurchase(sku: InvoiceSku): Promise<void> {
    if (!result) return;
    setIsPaying(true);
    try {
      const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, sku) : null;
      const { status } = await purchase(sku, { whenQuestionId: result.id });
      if (status === 'paid') await showPaidResult(sku, baselineRunId);
    } catch {
      setError(t('when.error'));
    } finally {
      setIsPaying(false);
    }
  }

  async function doRedeemCoupon(sku: InvoiceSku, code: string): Promise<void> {
    if (!result) throw new Error('no_result');
    const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, sku) : null;
    await redeemCoupon(sku, code, { whenQuestionId: result.id });
    await showPaidResult(sku, baselineRunId);
  }

  function fmtDate(iso: string): string {
    return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric' });
  }

  return (
    <MeSubpageShell title={t('when.title')} data-testid="when-page" right={<HistoryClock label={t('history.open')} testId="when-history" disabled={busy || !!diveLoading || isPaying} onClick={() => setShowHistory(true)} />}>
      <div className={styles.body}>
        <HowItWorks toolKey="when" />
        {history.resultId !== null && history.detail.isPending ? <p role="status">{t('common:states.loading')}</p> : history.resultId !== null && history.detail.isError ? <div>
          <p role="alert">{t(history.detail.error instanceof ApiError && history.detail.error.status === 404 ? 'history.notFound' : 'history.loadError')}</p>
          <SecondaryButton onClick={() => void history.detail.refetch()}>{t('history.retry')}</SecondaryButton>
          <SecondaryButton onClick={startNew}>{t('history.new')}</SecondaryButton>
        </div> : result === null ? (
          <>
            <p className={styles.prompt}>{t('when.prompt')}</p>
            <textarea
              className={styles.textarea}
              value={question}
              onChange={(e) => { if (history.resultId) history.open(null, true); setQuestion(e.target.value); }}
              placeholder={t('when.placeholder')}
              maxLength={300}
              data-testid="when-textarea"
            />
            {error && <p className={styles.error} role="alert">{error}</p>}
            <PrimaryButton
              onClick={() => void ask()}
              disabled={busy || question.trim().length < 3}
              data-testid="when-submit"
            >
              {busy ? t('when.working') : t('when.submit')}
            </PrimaryButton>
          </>
        ) : (
          <>
            <div className={styles.summary} data-testid="when-summary"><ReportMarkdown markdown={result.llmSummary ?? ''} /></div>

            <div className={styles.dates} data-testid="when-dates">
              {result.topDates.map((d) => (
                <div key={d.date} className={styles.dateWrap}>
                  <button
                    type="button"
                    disabled={!!diveLoading}
                    className={`${styles.date} ${diveDate === d.date ? styles.dateActive : ''}`}
                    onClick={() => void loadDeepDive(d.date)}
                    data-testid={`when-date-${d.date}`}
                  >
                    <span className={styles.dateLabel}>{fmtDate(d.date)}</span>
                    <span className={styles.dateScore}>{d.score}{t('when.scoreSuffix')}</span>
                    {d.driver && <span className={styles.dateDriver}>{d.driver}</span>}
                    <span className={styles.dateCaret}>{diveDate === d.date ? '▲' : '▼'}</span>
                  </button>
                  {diveDate === d.date && (
                    <div className={styles.dive} data-testid={`when-dive-${d.date}`}>
                      {diveLoading === d.date && <p className={styles.diveLoading}>{t('when.diveLoading')}</p>}
                      {diveText[d.date] && <div className={styles.diveText}><ReportMarkdown markdown={diveText[d.date]} /></div>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {error && <p className={styles.error} role="alert">{error}</p>}

            {/* Inline paid result (self-containment: replaces "sent to chat" notice) */}
            {(paidRunId || awaitingResult) && (
              <div className={styles.paidResult} data-testid="when-paid-result">
                {(awaitingResult || textResult.isLoading) && <p className={styles.diveLoading} data-testid="when-paid-loading">{t('when.diveLoading')}</p>}
                {textResult.isError && <p className={styles.error} role="alert">{t('when.error')}</p>}
                {textResult.data && <ReportMarkdown markdown={textResult.data.markdown} />}
              </div>
            )}

            {/* Hidden while a paid result is loading or shown; legacy paid horizons also hide repeat forecast purchases. */}
            {!paidRunId && !awaitingResult && !historyPaidForecast && (
              <div className={styles.ctaRow}>
                <PrimaryButton onClick={() => setInvoice('when_30')} data-testid="when-buy-30">
                  {t('when.more30', { stars: SKU_CATALOG['when_30'].stars })}
                </PrimaryButton>
                <PrimaryButton onClick={() => setInvoice('when_60')} data-testid="when-buy-60">
                  {t('when.more60', { stars: SKU_CATALOG['when_60'].stars })}
                </PrimaryButton>
              </div>
            )}

            <SecondaryButton
              onClick={startNew}
              disabled={!!diveLoading || isPaying}
              data-testid="when-reset"
            >
              {t('when.askAnother')}
            </SecondaryButton>
          </>
        )}
      </div>

      {showHistory && <ToolHistorySheet kind="when" currentId={result?.id ?? null} onClose={() => setShowHistory(false)} onOpen={id => { setShowHistory(false); history.open(id); }} onDeleted={id => { if (id === result?.id || id === history.resultId) { startNew(); setShowHistory(false); } }} />}

      {invoice && (
        <PaymentSheet
          skuCode={invoice}
          title={t(`when.invoice.${invoice}`, { stars: SKU_CATALOG[invoice].stars })}
          priceStars={SKU_CATALOG[invoice].stars}
          bullets={[]}
          isPaying={isPaying}
          onPay={() => void doPurchase(invoice)}
          onCancel={() => setInvoice(null)}
          onApplyCoupon={(code) => doRedeemCoupon(invoice, code)}
        />
      )}
    </MeSubpageShell>
  );
}
